/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (audit 7): when someone's media cannot be decrypted (their key did
 * not come: a ghost, an old client), they are silent and black and nothing
 * says why. Their tile now says "Ждём ключ шифрования…".
 * Upstream hid its encryption status as unreliable (MediaView: "less
 * broken"): keys often come a moment after the first frames. So only a
 * missing or wrong key that lasts ION_KEY_STATUS_DELAY_MS is shown, and
 * never "connecting".
 */

import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@vector-im/compound-web";
import { debounce, map, of, timer } from "rxjs";

import { EncryptionStatus } from "../state/media/MemberMediaViewModel";
import { type Behavior } from "../state/Behavior";
import { pick, type IonText } from "./callErrorTexts";

export const ION_KEY_STATUS_DELAY_MS = 5_000;

export const KEY_STATUS_TEXTS = {
  keyMissing: {
    ru: "Ждём ключ шифрования…",
    en: "Waiting for the encryption key…",
  },
  keyInvalid: {
    ru: "Не удаётся расшифровать звонок",
    en: "Cannot decrypt the call",
  },
} satisfies Record<string, IonText>;

/** The text for a lasting key problem, null otherwise. */
export function keyStatusText(status: EncryptionStatus): IonText | null {
  switch (status) {
    case EncryptionStatus.KeyMissing:
      return KEY_STATUS_TEXTS.keyMissing;
    case EncryptionStatus.KeyInvalid:
    case EncryptionStatus.PasswordInvalid:
      return KEY_STATUS_TEXTS.keyInvalid;
    default:
      return null;
  }
}

/** The status line of a tile, shown once a key problem lasts. */
export function useIonKeyStatus(
  encryptionStatus$: Behavior<EncryptionStatus> | undefined,
): ReactNode {
  const { i18n } = useTranslation();
  const [text, setText] = useState<IonText | null>(null);
  useEffect(() => {
    if (!encryptionStatus$) return;
    const s = encryptionStatus$
      .pipe(
        map(keyStatusText),
        // A problem shows after the delay; its end shows at once
        debounce((t) => (t ? timer(ION_KEY_STATUS_DELAY_MS) : of(0))),
      )
      .subscribe(setText);
    return (): void => {
      s.unsubscribe();
      setText(null);
    };
  }, [encryptionStatus$]);
  if (!text) return null;
  return (
    <Text as="span" size="sm" weight="medium" data-testid="ion-key-status">
      {pick(text, i18n.language)}
    </Text>
  );
}
